import { Component, computed, EventEmitter, HostListener, input, linkedSignal, Output, ChangeDetectionStrategy } from '@angular/core';

export type PaginationEvent = {
    currentIndex: number,
    newIndex: number,
    currentObjectIdentifier: number | string,
    newObjectIdentifier: number | string
};

enum PaginatorKeybindings {
    NavigateBackward = 'Shift + Alt + V',
    NavigateForward = 'Shift + Alt + D',
}

@Component( {
    selector: 'his-dialog-header-paginator',
    templateUrl: './his-dialog-header-paginator.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./his-dialog-header-paginator.component.css']
} )
export class HisDialogHeaderPaginatorComponent {
    public readonly MAX_ELEMENTS_TO_LOAD = 500; // The maximum number of elements to load.
    public readonly INDEX_FIRST_PAGE = 1;

    //Event emitted when navigating to the previous element.
    @Output() previousElement = new EventEmitter<PaginationEvent>();

    //Event emitted when navigating to the next element.
    @Output() nextElement = new EventEmitter<PaginationEvent>();

    readonly elements = input<any[]>();
    readonly initialIndex = input<number>( 0 );

    protected readonly navigateBackwardKeys = PaginatorKeybindings.NavigateBackward;
    protected readonly navigateForwardKeys = PaginatorKeybindings.NavigateForward;
	
    protected readonly currentIndex = linkedSignal( this.initialIndex );
    protected readonly currentPage = computed( () => this.currentIndex() + 1 ); // The currently displayed page number.

    protected readonly totalPages = computed( () => this.elements()?.length ); // The total number of pages. needs to be a signal as it is used in some `computed`
    protected readonly showButtons = computed( () => !!this.elements()?.length ); // Whether to show the buttons or not.
    protected readonly isFirstPage = computed( () => this.currentPage() === this.INDEX_FIRST_PAGE ); // Whether the current page is the first page or not.
    protected readonly isLastPage = computed( () => this.currentPage() === this.totalPages() ); // Whether the current page is the last page or not.
    protected readonly showPaginator = computed( () => this.totalPages() > this.MAX_ELEMENTS_TO_LOAD ); // Whether to show the paginator or not.

    onPreviousPage(): void {
        if ( this.isFirstPage() ) {
            return;
        }

        const current = this.currentIndex();
        const next = current - 1;
        this.currentIndex.set( next );
        this.navigateAndEmitPageChange( current, next, this.previousElement );
    }

    onNextPage(): void {
        if ( this.isLastPage() ) {
            return;
        }

        const current = this.currentIndex();
        const next = current + 1;
        this.currentIndex.set( next );
        this.navigateAndEmitPageChange( current, next, this.nextElement );
    }

    /**
    * Updates the current page and emits navigation event.
    * @private
    * @param {number} currentIndex - The current page index
    * @param {number} newIndex - The new page index to navigate to
    * @param {EventEmitter<PaginationEvent>} emitter - The event emitter to use
    */
    private navigateAndEmitPageChange( currentIndex: number, newIndex: number, emitter: EventEmitter<PaginationEvent> ): void {
        emitter.emit( {
            currentIndex: currentIndex,
            newIndex: newIndex,
            currentObjectIdentifier: this.getElementIdAtIndex( currentIndex ),
            newObjectIdentifier: this.getElementIdAtIndex( newIndex ),
        } );
    }

    private getElementIdAtIndex( index: number ): any | null {
        return this.elements()[index] || null;
    }

    @HostListener( 'window:keydown', ['$event'] )
    handleKeyboardEvent( event: KeyboardEvent ): void {
        if ( !( event.shiftKey && event.altKey ) ) {
            return;
        }

        switch ( event.key.toUpperCase() ) {
            case 'V':
                if ( !this.isFirstPage() ) {
                    event.preventDefault();
                    this.onPreviousPage();
                }
                break;
            case 'D':
                if ( !this.isLastPage() ) {
                    event.preventDefault();
                    this.onNextPage();
                }
                break;
        }
    }
}
